import _defineProperty from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _slicedToArray from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import _toConsumableArray from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/toConsumableArray.js";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { matches, isVisible, isFrame, getActiveElement, closest, contains } from './dom.js';
var selector = "input:not([type='hidden']):not([disabled]), select:not([disabled]), " + "textarea:not([disabled]), a[href], button:not([disabled]), [tabindex], " + "iframe, object, embed, area[href], audio[controls], video[controls], " + "[contenteditable]:not([contenteditable='false'])";

function hasNegativeTabIndex(element) {
  var tabIndex = parseInt(element.getAttribute("tabindex") || "0", 10);
  return tabIndex < 0;
}
/**
 * Checks whether `element` is focusable or not.
 * @example
 * isFocusable(document.querySelector("input")); // true
 * isFocusable(document.querySelector("input[tabindex='-1']")); // true
 * isFocusable(document.querySelector("input[hidden]")); // false
 * isFocusable(document.querySelector("input:disabled")); // false
 */


function isFocusable(element) {
  return matches(element, selector) && isVisible(element);
}
/**
 * Checks whether `element` is tabbable or not.
 * @example
 * isTabbable(document.querySelector("input")); // true
 * isTabbable(document.querySelector("input[tabindex='-1']")); // false
 * isTabbable(document.querySelector("input[hidden]")); // false
 * isTabbable(document.querySelector("input:disabled")); // false
 */


function isTabbable(element) {
  return isFocusable(element) && !hasNegativeTabIndex(element);
}
/**
 * Returns all the focusable elements in `container`.
 */


function getAllFocusableIn(container, includeContainer) {
  var elements = Array.from(container.querySelectorAll(selector));

  if (includeContainer) {
    elements.unshift(container);
  }

  var focusableElements = elements.filter(isFocusable);
  focusableElements.forEach(function (element, i) {
    if (isFrame(element) && element.contentDocument) {
      var frameBody = element.contentDocument.body;
      focusableElements.splice.apply(focusableElements, [i, 1].concat(_toConsumableArray(getAllFocusableIn(frameBody))));
    }
  });
  return focusableElements;
}
/**
 * Returns all the focusable elements in the document.
 */


function getAllFocusable(includeBody) {
  return getAllFocusableIn(document.body, includeBody);
}
/**
 * Returns the first focusable element in `container`.
 */


function getFirstFocusableIn(container, includeContainer) {
  var _getAllFocusableIn = getAllFocusableIn(container, includeContainer),
      _getAllFocusableIn2 = _slicedToArray(_getAllFocusableIn, 1),
      first = _getAllFocusableIn2[0];

  return first || null;
}
/**
 * Returns the first focusable element in the document.
 */


function getFirstFocusable(includeBody) {
  return getFirstFocusableIn(document.body, includeBody);
}
/**
 * Returns all the tabbable elements in `container`, including the container
 * itself.
 */


function getAllTabbableIn(container, includeContainer, fallbackToFocusable) {
  var elements = Array.from(container.querySelectorAll(selector));
  var tabbableElements = elements.filter(isTabbable);

  if (includeContainer && isTabbable(container)) {
    tabbableElements.unshift(container);
  }

  tabbableElements.forEach(function (element, i) {
    if (isFrame(element) && element.contentDocument) {
      var frameBody = element.contentDocument.body;
      var allFrameTabbable = getAllTabbableIn(frameBody, false, fallbackToFocusable);
      tabbableElements.splice.apply(tabbableElements, [i, 1].concat(_toConsumableArray(allFrameTabbable)));
    }
  });

  if (!tabbableElements.length && fallbackToFocusable) {
    return elements;
  }

  return tabbableElements;
}
/**
 * Returns all the tabbable elements in the document.
 */


function getAllTabbable(fallbackToFocusable) {
  return getAllTabbableIn(document.body, false, fallbackToFocusable);
}
/**
 * Returns the first tabbable element in `container`, including the container
 * itself if it's tabbable.
 */


function getFirstTabbableIn(container, includeContainer, fallbackToFocusable) {
  var _getAllTabbableIn = getAllTabbableIn(container, includeContainer, fallbackToFocusable),
      _getAllTabbableIn2 = _slicedToArray(_getAllTabbableIn, 1),
      first = _getAllTabbableIn2[0];

  return first || null;
}
/**
 * Returns the first tabbable element in the document.
 */


function getFirstTabbable(fallbackToFocusable) {
  return getFirstTabbableIn(document.body, false, fallbackToFocusable);
}
/**
 * Returns the last tabbable element in `container`, including the container
 * itself if it's tabbable.
 */


function getLastTabbableIn(container, includeContainer, fallbackToFocusable) {
  var allTabbable = getAllTabbableIn(container, includeContainer, fallbackToFocusable);
  return allTabbable[allTabbable.length - 1] || null;
}
/**
 * Returns the last tabbable element in the document.
 */


function getLastTabbable(fallbackToFocusable) {
  return getLastTabbableIn(document.body, false, fallbackToFocusable);
}
/**
 * Returns the next tabbable element in `container`.
 */


function getNextTabbableIn(container, includeContainer, fallbackToFirst, fallbackToFocusable) {
  var activeElement = getActiveElement(container);
  var allFocusable = getAllFocusableIn(container, includeContainer);
  var activeIndex = allFocusable.indexOf(activeElement);
  var nextFocusableElements = allFocusable.slice(activeIndex + 1);
  return nextFocusableElements.find(isTabbable) || (fallbackToFirst ? allFocusable.find(isTabbable) : null) || (fallbackToFocusable ? nextFocusableElements[0] : null) || null;
}
/**
 * Returns the next tabbable element in the document.
 */


function getNextTabbable(fallbackToFirst, fallbackToFocusable) {
  return getNextTabbableIn(document.body, false, fallbackToFirst, fallbackToFocusable);
}
/**
 * Returns the previous tabbable element in `container`.
 *
 */


function getPreviousTabbableIn(container, includeContainer, fallbackToLast, fallbackToFocusable) {
  var activeElement = getActiveElement(container);
  var allFocusable = getAllFocusableIn(container, includeContainer).reverse();
  var activeIndex = allFocusable.indexOf(activeElement);
  var previousFocusableElements = allFocusable.slice(activeIndex + 1);
  return previousFocusableElements.find(isTabbable) || (fallbackToLast ? allFocusable.find(isTabbable) : null) || (fallbackToFocusable ? previousFocusableElements[0] : null) || null;
}
/**
 * Returns the previous tabbable element in the document.
 */


function getPreviousTabbable(fallbackToFirst, fallbackToFocusable) {
  return getPreviousTabbableIn(document.body, false, fallbackToFirst, fallbackToFocusable);
}
/**
 * Returns the closest focusable element.
 */


function getClosestFocusable(element) {
  while (element && !isFocusable(element)) {
    element = closest(element, selector);
  }

  return element || null;
}
/**
 * Checks if `element` has focus. Elements that are referenced by
 * `aria-activedescendant` are also considered.
 * @example
 * hasFocus(document.getElementById("id"));
 */


function hasFocus(element) {
  var activeElement = getActiveElement(element);
  if (!activeElement) return false;
  if (activeElement === element) return true;
  var activeDescendant = activeElement.getAttribute("aria-activedescendant");
  if (!activeDescendant) return false;
  return activeDescendant === element.id;
}
/**
 * Checks if `element` has focus within. Elements that are referenced by
 * `aria-activedescendant` are also considered.
 * @example
 * hasFocusWithin(document.getElementById("id"));
 */


function hasFocusWithin(element) {
  var activeElement = getActiveElement(element);
  if (!activeElement) return false;
  if (contains(element, activeElement)) return true;
  var activeDescendant = activeElement.getAttribute("aria-activedescendant");
  if (!activeDescendant) return false;
  if (!("id" in element)) return false;
  if (activeDescendant === element.id) return true;
  return !!element.querySelector("#" + CSS.escape(activeDescendant));
}
/**
 * Focus on an element only if it's not already focused.
 */


function focusIfNeeded(element) {
  if (!hasFocusWithin(element) && isFocusable(element)) {
    element.focus();
  }
}
/**
 * Disable focus on `element`.
 */


function disableFocus(element) {
  var _element$getAttribute;

  var currentTabindex = (_element$getAttribute = element.getAttribute("tabindex")) != null ? _element$getAttribute : "";
  element.setAttribute("data-tabindex", currentTabindex);
  element.setAttribute("tabindex", "-1");
}
/**
 * Makes elements inside container not tabbable.
 */


function disableFocusIn(container, includeContainer) {
  var tabbableElements = getAllTabbableIn(container, includeContainer);
  tabbableElements.forEach(disableFocus);
}
/**
 * Restores tabbable elements inside container that were affected by
 * disableFocusIn.
 */


function restoreFocusIn(container) {
  var elements = container.querySelectorAll("[data-tabindex]");

  var restoreTabIndex = function restoreTabIndex(element) {
    var tabindex = element.getAttribute("data-tabindex");
    element.removeAttribute("data-tabindex");

    if (tabindex) {
      element.setAttribute("tabindex", tabindex);
    } else {
      element.removeAttribute("tabindex");
    }
  };

  if (container.hasAttribute("data-tabindex")) {
    restoreTabIndex(container);
  }

  elements.forEach(restoreTabIndex);
}
/**
 * Focus on element and scroll into view.
 */


function focusIntoView(element, options) {
  if (!("scrollIntoView" in element)) {
    element.focus();
  } else {
    element.focus({
      preventScroll: true
    });
    element.scrollIntoView(_objectSpread({
      block: "nearest",
      inline: "nearest"
    }, options));
  }
}

export { disableFocus, disableFocusIn, focusIfNeeded, focusIntoView, getAllFocusable, getAllFocusableIn, getAllTabbable, getAllTabbableIn, getClosestFocusable, getFirstFocusable, getFirstFocusableIn, getFirstTabbable, getFirstTabbableIn, getLastTabbable, getLastTabbableIn, getNextTabbable, getNextTabbableIn, getPreviousTabbable, getPreviousTabbableIn, hasFocus, hasFocusWithin, isFocusable, isTabbable, restoreFocusIn };